"use client"

import { ProjectConfig } from "./ProjectConfigurator"
import { Button } from "@/components/ui/button"
import { Typography } from "@/components/typography"
import { Card, CardContent } from "@/components/ui/card"

interface SummaryViewProps {
  config: ProjectConfig
  onBack: () => void
  onNext: () => void
}

export function SummaryView({ config, onBack, onNext }: SummaryViewProps) {
  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      website: "Website",
      app: "App",
      tool: "Tool",
      design: "Design Work",
      integrations: "Integrations",
      consulting: "Business Consulting",
      unknown: "I Don't Know",
    }
    return labels[type] || type
  }

  return (
    <div className="space-y-8">
      <div>
        <Typography variant="h2" as="h2" className="mb-4">
          Review Your Project Details
        </Typography>
        <Typography variant="body" className="text-tk-slate/80">
          Review your selections before submitting. You can go back to make changes.
        </Typography>
      </div>

      <div className="space-y-6">
        {/* Project Types */}
        <Card className="border-tk-slate/10 bg-tk-linen">
          <CardContent className="pt-6">
            <Typography variant="h3" as="h3" className="mb-3 text-tk-onyx">
              Project Types
            </Typography>
            <div className="flex flex-wrap gap-2">
              {(config.projectTypes || []).map((type) => (
                <span
                  key={type}
                  className="rounded-full bg-tk-teal/10 px-3 py-1 text-sm font-medium text-tk-teal font-ui"
                >
                  {getTypeLabel(type)}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Website Details */}
        {config.website && (
          <Card className="border-tk-slate/10 bg-tk-linen">
            <CardContent className="pt-6">
              <Typography variant="h3" as="h3" className="mb-3 text-tk-onyx">
                Website Details
              </Typography>
              <div className="space-y-2 text-sm text-tk-slate/80 font-body">
                {config.website.type && <p>Type: {config.website.type}</p>}
                {config.website.currentSituation && (
                  <p>Situation: {config.website.currentSituation}</p>
                )}
                {config.website.design && <p>Design: {config.website.design}</p>}
                {config.website.painPoints && config.website.painPoints.length > 0 && (
                  <p>Pain points: {config.website.painPoints.join(", ")}</p>
                )}
                {config.website.features && config.website.features.length > 0 && (
                  <p>Features: {config.website.features.join(", ")}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* App Details */}
        {config.app && (
          <Card className="border-tk-slate/10 bg-tk-linen">
            <CardContent className="pt-6">
              <Typography variant="h3" as="h3" className="mb-3 text-tk-onyx">
                App Details
              </Typography>
              <div className="space-y-2 text-sm text-tk-slate/80 font-body">
                {config.app.type && <p>Type: {config.app.type}</p>}
                {config.app.users && <p>Users: {config.app.users}</p>}
                {config.app.purpose && <p>Purpose: {config.app.purpose}</p>}
                {config.app.painPoints && config.app.painPoints.length > 0 && (
                  <p>Pain points: {config.app.painPoints.join(", ")}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tool Details */}
        {config.tool && (
          <Card className="border-tk-slate/10 bg-tk-linen">
            <CardContent className="pt-6">
              <Typography variant="h3" as="h3" className="mb-3 text-tk-onyx">
                Tool Details
              </Typography>
              <div className="space-y-2 text-sm text-tk-slate/80 font-body">
                {config.tool.type && <p>Type: {config.tool.type}</p>}
                {config.tool.painPoints && config.tool.painPoints.length > 0 && (
                  <p>Pain points: {config.tool.painPoints.join(", ")}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Design Details */}
        {config.design && (
          <Card className="border-tk-slate/10 bg-tk-linen">
            <CardContent className="pt-6">
              <Typography variant="h3" as="h3" className="mb-3 text-tk-onyx">
                Design Work Details
              </Typography>
              <div className="space-y-2 text-sm text-tk-slate/80 font-body">
                {config.design.type && <p>Type: {config.design.type}</p>}
                {config.design.currentState && <p>Current state: {config.design.currentState}</p>}
                {config.design.painPoints && config.design.painPoints.length > 0 && (
                  <p>Pain points: {config.design.painPoints.join(", ")}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Integrations Details */}
        {config.integrations && (
          <Card className="border-tk-slate/10 bg-tk-linen">
            <CardContent className="pt-6">
              <Typography variant="h3" as="h3" className="mb-3 text-tk-onyx">
                Integrations Details
              </Typography>
              <div className="space-y-2 text-sm text-tk-slate/80 font-body">
                {config.integrations.systems && config.integrations.systems.length > 0 && (
                  <p>Systems: {config.integrations.systems.join(", ")}</p>
                )}
                {config.integrations.painPoints && config.integrations.painPoints.length > 0 && (
                  <p>Pain points: {config.integrations.painPoints.join(", ")}</p>
                )}
                {config.integrations.goal && <p>Goal: {config.integrations.goal}</p>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Consulting Details */}
        {config.consulting && (
          <Card className="border-tk-slate/10 bg-tk-linen">
            <CardContent className="pt-6">
              <Typography variant="h3" as="h3" className="mb-3 text-tk-onyx">
                Business Consulting Details
              </Typography>
              <div className="space-y-2 text-sm text-tk-slate/80 font-body">
                {config.consulting.type && <p>Type: {config.consulting.type}</p>}
                {config.consulting.currentSituation && (
                  <p>Current situation: {config.consulting.currentSituation}</p>
                )}
                {config.consulting.painPoints && config.consulting.painPoints.length > 0 && (
                  <p>Pain points: {config.consulting.painPoints.join(", ")}</p>
                )}
                {config.consulting.goals && config.consulting.goals.length > 0 && (
                  <p>Goals: {config.consulting.goals.join(", ")}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unknown Details */}
        {config.unknown && (
          <Card className="border-tk-slate/10 bg-tk-linen">
            <CardContent className="pt-6">
              <Typography variant="h3" as="h3" className="mb-3 text-tk-onyx">
                Project Details
              </Typography>
              <div className="space-y-2 text-sm text-tk-slate/80 font-body">
                {config.unknown.whatsNotWorking && (
                  <p>What&apos;s not working: {config.unknown.whatsNotWorking}</p>
                )}
                {config.unknown.tryingToAchieve && (
                  <p>Goal: {config.unknown.tryingToAchieve}</p>
                )}
                {config.unknown.painPoints && config.unknown.painPoints.length > 0 && (
                  <p>Pain points: {config.unknown.painPoints.join(", ")}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Info */}
        {config.additional && (
          <Card className="border-tk-slate/10 bg-tk-linen">
            <CardContent className="pt-6">
              <Typography variant="h3" as="h3" className="mb-3 text-tk-onyx">
                Additional Information
              </Typography>
              <div className="space-y-2 text-sm text-tk-slate/80 font-body">
                {config.additional.timeline && <p>Timeline: {config.additional.timeline}</p>}
                {config.additional.budget && <p>Budget: {config.additional.budget}</p>}
                {config.additional.anythingElse && (
                  <p>Notes: {config.additional.anythingElse}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="flex justify-between pt-6">
        <Button
          onClick={onBack}
          variant="outline"
          className="rounded-full border-tk-slate/25 bg-tk-linen text-sm text-tk-slate hover:border-tk-teal hover:text-tk-onyx font-ui"
        >
          ← Back
        </Button>
        <Button
          onClick={onNext}
          className="rounded-full bg-tk-teal px-8 py-2 text-sm font-semibold text-tk-linen hover:bg-tk-teal/90 font-ui"
        >
          Continue →
        </Button>
      </div>
    </div>
  )
}

